import { createContext, useContext } from "react";

export interface ImpersonationTarget {
  userId: string;
  name: string;
  email: string;
  sessionId: string;
  expiresAt: number;
}

export interface ImpersonationState {
  impersonating: ImpersonationTarget | null;
  startImpersonation: (target: ImpersonationTarget) => void;
  stopImpersonation: () => void;
  isImpersonating: boolean;
  effectiveUserId: string | null;
}

export const ImpersonationContext = createContext<ImpersonationState | null>(null);

export function useImpersonation(): ImpersonationState {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error("useImpersonation must be used within an ImpersonationProvider");
  }
  return context;
}

const STORAGE_KEY = "bac_impersonation";
const ORIGIN_KEY = "bac_impersonation_origin";

export function loadImpersonationFromStorage(): ImpersonationTarget | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as ImpersonationTarget;
    if (data.expiresAt < Date.now()) {
      clearImpersonationFromStorage();
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

export function saveImpersonationToStorage(target: ImpersonationTarget): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(target));
}

export function clearImpersonationFromStorage(): void {
  sessionStorage.removeItem(STORAGE_KEY);
  clearImpersonationOrigin();
}

// Task #6273 — remember where the admin was when impersonation started so
// "End session" can return them there instead of the hardcoded /admin/users.
export function saveImpersonationOrigin(location: string): void {
  try {
    if (!location.startsWith("/")) return;
    sessionStorage.setItem(ORIGIN_KEY, location);
  } catch {
    // sessionStorage unavailable — fall back silently, exit uses /admin/users.
  }
}

export function loadImpersonationOrigin(): string | null {
  try {
    const raw = sessionStorage.getItem(ORIGIN_KEY);
    if (!raw || !raw.startsWith("/")) return null;
    return raw;
  } catch {
    return null;
  }
}

export function clearImpersonationOrigin(): void {
  try {
    sessionStorage.removeItem(ORIGIN_KEY);
  } catch {
    // ignore
  }
}
